/**
 * Image utility functions for generating public URLs
 */

// Get Supabase URL from environment variable, with fallback to production URL
// This ensures local development uses the local Supabase instance
export const SUPABASE_URL =
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  "https://jlmsyojjgtnnookzxdtf.supabase.co";
// Public showcase assets are stored in the production project, including when
// local development uses a separate Supabase instance.
export const PUBLIC_SHOWCASE_STORAGE_URL =
  "https://jlmsyojjgtnnookzxdtf.supabase.co/storage/v1/object/public";
const SITE_URL =
  process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com";
export const PUBLIC_IMAGE_CACHE_SECONDS = 31_536_000;
export const PUBLIC_IMAGE_CACHE_CONTROL = `public, max-age=${PUBLIC_IMAGE_CACHE_SECONDS}, immutable`;
export const PUBLIC_IMAGE_CDN_CACHE_CONTROL = `public, max-age=${PUBLIC_IMAGE_CACHE_SECONDS}`;

export type PublicImageFit = "cover" | "inside";
export type PublicImageFormat = "jpeg" | "webp" | "png";

export interface PublicImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  fit?: PublicImageFit;
  format?: PublicImageFormat;
}

const GENERATED_IMAGE_PUBLIC_MARKER =
  "/storage/v1/object/public/generated-images/";

/**
 * Recover the object path expected by `/api/generate-image` from a generated
 * image's public URL. Agent sessions persist the display URL, while sequential
 * back/modification requests need the bucket-relative path. Keeping this
 * conversion strict prevents arbitrary remote URLs or traversal segments from
 * becoming storage reads.
 */
export function getGeneratedImageStoragePath(
  imageUrlOrPath: string | null | undefined
): string | null {
  const value = imageUrlOrPath?.trim();
  if (!value) return null;

  let encodedPath: string;
  if (/^https?:\/\//i.test(value)) {
    let parsed: URL;
    try {
      parsed = new URL(value);
    } catch {
      return null;
    }
    const markerIndex = parsed.pathname.indexOf(GENERATED_IMAGE_PUBLIC_MARKER);
    if (markerIndex === -1) return null;
    encodedPath = parsed.pathname.slice(
      markerIndex + GENERATED_IMAGE_PUBLIC_MARKER.length
    );
  } else {
    encodedPath = value.replace(/^\/+/, "").split(/[?#]/, 1)[0] ?? "";
  }

  let path: string;
  try {
    path = decodeURIComponent(encodedPath);
  } catch {
    return null;
  }

  const segments = path.replace(/\\/g, "/").split("/");
  if (
    segments.length < 2 ||
    segments.some(segment => !segment || segment === "." || segment === "..")
  ) {
    return null;
  }

  return segments.join("/");
}

const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

/**
 * Generate a public URL for an image stored in Supabase storage.
 *
 * Returns the direct Supabase `/storage/v1/object/public` URL. `next/image`
 * consumers should pass this straight through so the optimizer fetches the
 * origin from Supabase's CDN instead of hopping through the owned-domain proxy
 * route. Metadata/feed/sitemap/email consumers that need the owned domain must
 * wrap the result in `convertToProxiedUrl()` (or `getOptimizedPublicImageUrl`).
 */
export function getPublicImageUrl(
  imagePath: string | null | undefined
): string {
  if (!imagePath) return "";

  if (imagePath.startsWith("http")) {
    return imagePath;
  }

  return `${SUPABASE_URL}/storage/v1/object/public/generated-images/${imagePath}`;
}

/**
 * Build a domain-owned optimized image URL for non-Next.js consumers.
 *
 * Use this for email, social metadata, feeds, and places where <Image> cannot
 * generate a `/_next/image` variant. React UI should usually pass the canonical
 * URL from `getPublicImageUrl()` into `next/image` instead.
 */
export function getOptimizedPublicImageUrl(
  imageUrl: string | null | undefined,
  options: PublicImageTransformOptions = {},
  baseUrl: string = SITE_URL
): string {
  if (!imageUrl) return "";

  const resolvedImageUrl = imageUrl.startsWith("/")
    ? new URL(imageUrl, baseUrl).toString()
    : convertToProxiedUrl(imageUrl);
  const optimizedUrl = new URL("/api/images", baseUrl);

  optimizedUrl.searchParams.set("url", resolvedImageUrl);

  if (options.width) {
    optimizedUrl.searchParams.set("w", String(options.width));
  }

  if (options.height) {
    optimizedUrl.searchParams.set("h", String(options.height));
  }

  if (options.quality) {
    optimizedUrl.searchParams.set("q", String(options.quality));
  }

  if (options.fit) {
    optimizedUrl.searchParams.set("fit", options.fit);
  }

  if (options.format) {
    optimizedUrl.searchParams.set("format", options.format);
  }

  return optimizedUrl.toString();
}

/**
 * Build a bounded, owned-domain image URL for social/OG/Twitter metadata and
 * JSON-LD from an arbitrary image URL or storage path.
 *
 * Supabase storage images are routed through the transform pipeline at the
 * requested dimensions; anything else (already-hosted or unknown host) is
 * returned as an absolute URL, falling back to the site default OG image.
 */
export function getMetadataImageUrl(
  imageUrl: string | null | undefined,
  options: PublicImageTransformOptions = {},
  baseUrl: string = SITE_URL
): string {
  const defaultOgImage = new URL("/og-image.jpg", baseUrl).toString();
  if (!imageUrl) return defaultOgImage;

  const resolvedImageUrl = imageUrl.startsWith("/")
    ? new URL(imageUrl, baseUrl).toString()
    : imageUrl;

  try {
    const parsedImageUrl = new URL(resolvedImageUrl);
    if (parsedImageUrl.pathname.startsWith("/storage/v1/object/public/")) {
      return getOptimizedPublicImageUrl(
        parsedImageUrl.toString(),
        {
          width: 1200,
          height: 630,
          quality: 82,
          fit: "cover",
          format: "jpeg",
          ...options,
        },
        baseUrl
      );
    }
  } catch {
    return defaultOgImage;
  }

  return resolvedImageUrl;
}

/**
 * Convert a Supabase image URL to use our domain (production only)
 * In development, keep the Supabase URL for Next.js image optimization compatibility
 */
export function convertToProxiedUrl(
  imageUrl: string | null | undefined
): string {
  if (!imageUrl) return "";

  // In development, don't proxy - use direct Supabase URLs
  if (IS_DEVELOPMENT) {
    return imageUrl;
  }

  // Extract the Supabase URL from the environment or use the production URL
  const supabaseUrl =
    process.env.NEXT_PUBLIC_SUPABASE_URL ||
    "https://jlmsyojjgtnnookzxdtf.supabase.co";
  return imageUrl.replace(supabaseUrl, SITE_URL);
}
